import React, { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import {
  CreateGHADiscountRuleBody,
  GHADiscountRule,
  GHADiscountRuleAccountType,
  UpdateGHADiscountRuleBody,
} from '~/services/ConnectionGoogleService';
import { parseDayjs } from '~/services/TimeService';

import {
  GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS,
  GHA_DISCOUNT_APPLICATION_OPTIONS,
  GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX,
  GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX,
  GHA_DISCOUNT_TYPE_OPTIONS,
  MAX_PERCENTAGE_DISCOUNT,
} from './constants';
import { GHADiscountRuleFormState, toCreateGHADiscountRuleBody, toUpdateGHADiscountRuleBody } from './utils';

interface Props {
  open: boolean;
  rule?: GHADiscountRule | null;
  initialPropertyId?: string;
  lockPropertyId?: boolean;
  accountTypeOptions?: Array<{ value: GHADiscountRuleAccountType; label: string }>;
  defaultAccountType?: GHADiscountRuleAccountType;
  isSaving: boolean;
  onClose: () => void;
  onCreate: (input: CreateGHADiscountRuleBody) => Promise<void>;
  onUpdate: (input: UpdateGHADiscountRuleBody) => Promise<void>;
}

type FormField = keyof GHADiscountRuleFormState;

function formatDateTimeLocal(value?: string | null) {
  return value ? parseDayjs(value).format('YYYY-MM-DDTHH:mm') : '';
}

function getInitialState(
  rule?: GHADiscountRule | null,
  defaults: {
    propertyId?: string;
    accountType?: GHADiscountRuleAccountType;
  } = {},
): GHADiscountRuleFormState {
  return {
    propertyId: rule?.propertyId ?? defaults.propertyId ?? '',
    accountType: rule?.accountType ?? defaults.accountType ?? 'lpp',
    discountType: rule?.discountType ?? 'fixed_amount',
    discountApplication: rule?.discountApplication ?? 'per_stay',
    discountValue: rule?.discountValue !== undefined ? String(rule.discountValue) : '',
    minAdvanceBookingWindow: rule?.minAdvanceBookingWindow !== undefined ? String(rule.minAdvanceBookingWindow) : '0',
    minLengthOfStay: rule?.minLengthOfStay !== undefined ? String(rule.minLengthOfStay) : '1',
    enabled: rule?.enabled ?? true,
    startsAt: formatDateTimeLocal(rule?.startsAt),
    endsAt: formatDateTimeLocal(rule?.endsAt),
  };
}

function buildErrors(form: GHADiscountRuleFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  const discountValue = Number(form.discountValue);
  const minAdvanceBookingWindow = Number(form.minAdvanceBookingWindow);
  const minLengthOfStay = Number(form.minLengthOfStay);

  if (!form.propertyId.trim()) {
    errors.propertyId = 'Property ID is required';
  }
  if (!Number.isFinite(discountValue) || discountValue <= 0) {
    errors.discountValue = 'Discount value must be greater than 0';
  }
  if (form.discountType === 'percentage' && discountValue > MAX_PERCENTAGE_DISCOUNT) {
    errors.discountValue = `Percentage discount cannot exceed ${MAX_PERCENTAGE_DISCOUNT}%`;
  }
  if (
    !Number.isInteger(minAdvanceBookingWindow) ||
    minAdvanceBookingWindow < 0 ||
    minAdvanceBookingWindow > GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX
  ) {
    errors.minAdvanceBookingWindow = `Advance booking window must be 0-${GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX}`;
  }
  if (
    !Number.isInteger(minLengthOfStay) ||
    minLengthOfStay < 1 ||
    minLengthOfStay > GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX
  ) {
    errors.minLengthOfStay = `Length of stay must be 1-${GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX}`;
  }
  if (form.discountType === 'percentage' && form.discountApplication === 'per_night') {
    errors.discountApplication = 'Percentage discounts cannot be applied per night';
  }
  if (form.startsAt && form.endsAt && parseDayjs(form.startsAt).isAfter(parseDayjs(form.endsAt))) {
    errors.endsAt = 'End date must be after start date';
  }

  return errors;
}

function GHADiscountRuleDialog({
  open,
  rule,
  initialPropertyId,
  lockPropertyId,
  accountTypeOptions = GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS,
  defaultAccountType,
  isSaving,
  onClose,
  onCreate,
  onUpdate,
}: Props) {
  const initialAccountType = defaultAccountType ?? accountTypeOptions[0]?.value;
  const [form, setForm] = useState<GHADiscountRuleFormState>(() =>
    getInitialState(rule, { propertyId: initialPropertyId, accountType: initialAccountType }),
  );
  const [touchedFields, setTouchedFields] = useState<Partial<Record<FormField, boolean>>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(getInitialState(rule, { propertyId: initialPropertyId, accountType: initialAccountType }));
      setTouchedFields({});
      setHasSubmitted(false);
    }
  }, [initialAccountType, initialPropertyId, open, rule]);

  const errors = useMemo(() => buildErrors(form), [form]);
  const displayedErrors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(errors).filter(([field]) => hasSubmitted || touchedFields[field as FormField]),
      ) as Record<string, string>,
    [errors, hasSubmitted, touchedFields],
  );
  const isValid = Object.keys(errors).length === 0;

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    setHasSubmitted(true);
    if (!isValid) {
      return;
    }

    if (rule) {
      await onUpdate(toUpdateGHADiscountRuleBody(form));
    } else {
      await onCreate(toCreateGHADiscountRuleBody(form));
    }
  };

  const updateForm = <K extends keyof GHADiscountRuleFormState>(key: K, value: GHADiscountRuleFormState[K]) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'discountType' && value === 'percentage' ? { discountApplication: 'per_stay' as const } : {}),
    }));
  };

  const markFieldTouched = (field: FormField) => {
    setTouchedFields((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{rule ? 'Edit GHA Discount Rule' : 'Create GHA Discount Rule'}</DialogTitle>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Property ID"
              value={form.propertyId}
              onChange={(event) => updateForm('propertyId', event.target.value)}
              onBlur={() => markFieldTouched('propertyId')}
              required
              disabled={lockPropertyId}
              fullWidth
              error={!!displayedErrors.propertyId}
              helperText={displayedErrors.propertyId || 'UUID of the Google-connected property.'}
            />
            <FormControl fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                label="Account Type"
                value={form.accountType}
                onChange={(event) => updateForm('accountType', event.target.value)}
                onBlur={() => markFieldTouched('accountType')}
                disabled={!!rule}
              >
                {accountTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  label="Discount Type"
                  value={form.discountType}
                  onChange={(event) => updateForm('discountType', event.target.value)}
                  onBlur={() => markFieldTouched('discountType')}
                >
                  {GHA_DISCOUNT_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth error={!!displayedErrors.discountApplication}>
                <InputLabel>Application</InputLabel>
                <Select
                  label="Application"
                  value={form.discountApplication}
                  onChange={(event) => updateForm('discountApplication', event.target.value)}
                  onBlur={() => markFieldTouched('discountApplication')}
                >
                  {GHA_DISCOUNT_APPLICATION_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={form.discountType === 'percentage' && option.value === 'per_night'}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {!!displayedErrors.discountApplication && (
                  <FormHelperText>{displayedErrors.discountApplication}</FormHelperText>
                )}
              </FormControl>
            </Stack>
            <TextField
              label="Discount Value"
              type="number"
              value={form.discountValue}
              onChange={(event) => updateForm('discountValue', event.target.value)}
              onBlur={() => markFieldTouched('discountValue')}
              required
              fullWidth
              error={!!displayedErrors.discountValue}
              helperText={displayedErrors.discountValue || 'Percentage or fixed amount, depending on rule type.'}
              inputProps={{
                min: 0,
                ...(form.discountType === 'percentage' ? { max: MAX_PERCENTAGE_DISCOUNT } : {}),
                step: 0.01,
              }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Min Advance Booking Window"
                type="number"
                value={form.minAdvanceBookingWindow}
                onChange={(event) => updateForm('minAdvanceBookingWindow', event.target.value)}
                onBlur={() => markFieldTouched('minAdvanceBookingWindow')}
                required
                fullWidth
                error={!!displayedErrors.minAdvanceBookingWindow}
                helperText={displayedErrors.minAdvanceBookingWindow || 'Minimum days before check-in.'}
                inputProps={{ min: 0, max: GHA_DISCOUNT_MIN_ADVANCE_BOOKING_WINDOW_MAX, step: 1 }}
              />
              <TextField
                label="Min Length of Stay"
                type="number"
                value={form.minLengthOfStay}
                onChange={(event) => updateForm('minLengthOfStay', event.target.value)}
                onBlur={() => markFieldTouched('minLengthOfStay')}
                required
                fullWidth
                error={!!displayedErrors.minLengthOfStay}
                helperText={displayedErrors.minLengthOfStay || 'Minimum nights.'}
                inputProps={{ min: 1, max: GHA_DISCOUNT_MIN_LENGTH_OF_STAY_MAX, step: 1 }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Starts At"
                type="datetime-local"
                value={form.startsAt}
                onChange={(event) => updateForm('startsAt', event.target.value)}
                onBlur={() => markFieldTouched('startsAt')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Ends At"
                type="datetime-local"
                value={form.endsAt}
                onChange={(event) => updateForm('endsAt', event.target.value)}
                onBlur={() => markFieldTouched('endsAt')}
                fullWidth
                error={!!displayedErrors.endsAt}
                helperText={displayedErrors.endsAt}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox checked={form.enabled} onChange={(event) => updateForm('enabled', event.target.checked)} />
                }
                label="Enabled"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={isSaving}>
            {rule ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default GHADiscountRuleDialog;
